/**
 * Share feature constants + small pure helpers.
 *
 * Field limits mirror backend `ShareConstants`. Filter option value lists are
 * derived from the canonical app enums so the form / viewer controls stay in
 * sync with the rest of the app.
 */
import { TaskStatus, Priority } from '@/types';
import { ObstructionType } from '@/types/obstructions/enums/ObstructionType';
import { ObstructionStatus } from '@/types/obstructions/enums/ObstructionStatus';
import { SeverityLevel } from '@/types/obstructions/enums/SeverityLevel';
import { TrafficImpact } from '@/types/obstructions/enums/TrafficImpact';
import { NOTE_COLOR_SWATCHES } from '@/constants/notes';

// ---- Field limits (mirror backend ShareConstants) ----
export const SHARE_NAME_MAX_LENGTH = 150;
export const SHARE_DESCRIPTION_MAX_LENGTH = 1000;
export const SHARE_PASSWORD_MIN_LENGTH = 8;
export const SHARE_PASSWORD_MAX_LENGTH = 200;

// ---- Embed iframe defaults ----
const EMBED_IFRAME_WIDTH = '100%';
const EMBED_IFRAME_HEIGHT = 600;

// ---- Filter option value lists (PascalCase enum strings, as the API expects) ----
export const SHARE_TASK_STATUS_VALUES: string[] = Object.values(TaskStatus);
export const SHARE_TASK_PRIORITY_VALUES: string[] = Object.values(Priority);
export const SHARE_OBSTRUCTION_TYPE_VALUES: string[] =
  Object.values(ObstructionType);
export const SHARE_OBSTRUCTION_STATUS_VALUES: string[] =
  Object.values(ObstructionStatus);
export const SHARE_OBSTRUCTION_SEVERITY_VALUES: string[] =
  Object.values(SeverityLevel);
export const SHARE_OBSTRUCTION_IMPACT_VALUES: string[] =
  Object.values(TrafficImpact);
export const SHARE_NOTE_COLOR_VALUES: string[] = [...NOTE_COLOR_SWATCHES];

/**
 * Builds an absolute URL from a relative share/embed path (e.g. `/share/abc`).
 * The backend returns relative `shareUrl` / `embedUrl`; the UI shows the full
 * origin-qualified link for copy / embed.
 */
export function toAbsoluteUrl(relativePath: string): string {
  if (/^https?:\/\//i.test(relativePath)) return relativePath;
  const origin =
    typeof window !== 'undefined' && window.location
      ? window.location.origin
      : '';
  return `${origin}${relativePath}`;
}

/** Builds the iframe embed snippet for a given embed URL. */
export function buildEmbedSnippet(embedUrl: string, title: string): string {
  const absolute = toAbsoluteUrl(embedUrl);
  const safeTitle = title.replace(/"/g, '&quot;');
  return `<iframe src="${absolute}" width="${EMBED_IFRAME_WIDTH}" height="${EMBED_IFRAME_HEIGHT}" style="border:0" title="${safeTitle}" loading="lazy"></iframe>`;
}
